// src/components/payment/DriverPaymentTransactionList.js
import React from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Button
} from '@mui/material';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import BoltIcon from '@mui/icons-material/Bolt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { format } from 'date-fns';
import LoadingSpinner from '../common/Loadingspinner';
import ErrorAlert from '../common/ErrorAlert';


const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString("en-GB");
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return `$${parseFloat(amount).toFixed(2)}`;
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
};

const getPaymentStatusColor = (paymentStatus) => {
  switch (paymentStatus?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
};

// Helper function to determine if payment button should be shown
const shouldShowPaymentButton = (transaction) => {
  const paymentStatus = transaction.PaymentTransactionPaymentStatus?.toLowerCase();
  return paymentStatus && ['pending', 'failed'].includes(paymentStatus);
};

// Helper function to determine if retry payment is possible
const canRetryPayment = (transaction) => {
  return shouldShowPaymentButton(transaction) && transaction.PaymentTransactionSessionId;
};

const DriverPaymentTransactionList = ({ 
  paymentTransactions, 
  isLoading, 
  isError, 
  error 
}) => {
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading payment transactions'} />;
  }

  if (!paymentTransactions || paymentTransactions.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No payment transactions found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your payment history will appear here after you make payments for charging sessions
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent sx={{ padding: 0 }}>
        <TableContainer component={Paper} sx={{ maxHeight: 650, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="120px">Transaction ID</TableCell>
                <TableCell width="140px">Session ID</TableCell>
                <TableCell width="180px">Transaction Date</TableCell>
                <TableCell width="120px">Amount</TableCell>
                <TableCell width="140px">Payment Method</TableCell>
                <TableCell width="120px">Transaction Status</TableCell>
                <TableCell width="120px">Payment Status</TableCell>
                <TableCell width="150px">Charger Info</TableCell>
                <TableCell width="160px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentTransactions.map((transaction) => (
                <TableRow key={transaction.PaymentTransactionId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      #{transaction.PaymentTransactionId}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    {transaction.PaymentTransactionSessionId ? (
                      <Button
                        component={Link}
                        to={`/sessions/${transaction.PaymentTransactionSessionId}`}
                        variant="text"
                        size="small"
                        startIcon={<BoltIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        #{transaction.PaymentTransactionSessionId}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(transaction.PaymentTransactionDateTime)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        {formatCurrency(transaction.PaymentTransactionAmount)}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CreditCardIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {transaction.PaymentTransactionMethodUsed || 'Card'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip 
                      label={transaction.PaymentTransactionStatus || 'Unknown'} 
                      color={getStatusColor(transaction.PaymentTransactionStatus)}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Chip 
                      label={transaction.PaymentTransactionPaymentStatus || 'Unknown'} 
                      color={getPaymentStatusColor(transaction.PaymentTransactionPaymentStatus)}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2" noWrap>
                        {transaction.PaymentTransactionChargerId || '-'}
                      </Typography>
                      {transaction.PaymentTransactionSiteId && (
                        <Typography variant="caption" color="text.secondary">
                          Site {transaction.PaymentTransactionSiteId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {/* View Details Button */}
                      <Tooltip title="View Transaction Details">
                        <IconButton 
                          component={Link} 
                          to={`/payment/transactions/${transaction.PaymentTransactionId}`}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Retry Payment Button - Only show if payment can be retried */}
                      {canRetryPayment(transaction) && (
                        <Tooltip title="Retry Payment">
                          <IconButton 
                            component={Link} 
                            to={`/sessions/${transaction.PaymentTransactionSessionId}/payment`}
                            color="success"
                            size="small"
                            sx={{
                              bgcolor: 'success.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'success.dark',
                              }
                            }}
                          >
                            <PaymentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default DriverPaymentTransactionList;